import { useCallback, useContext } from 'react';
import { AddonsContext } from './addonsContext';
import { useBi } from './useBi';
import type {
  BaseAddonEventPayload,
  InstallAddonEventPayload,
  UninstallAddonEventPayload,
} from './useBi';

export const useAddons = () => {
  const context = useContext(AddonsContext);
  const bi = useBi();

  if (!context) {
    throw new Error(
      `useAddons() hook must be used inside AddonsContext.Provider component.`,
    );
  }

  const {
    state,
    isInstallationInProgress,
    openAddonPanel,
    closeAddonPanel,
    isInstalled,
    fetchAddons,
    handleInstall,
    handleUninstall,
  } = context;

  const installAddon = useCallback(
    async (
      appDefinitionId: string,
      biData: { origin: BaseAddonEventPayload['origin'] },
    ) => {
      const biPayload: InstallAddonEventPayload = {
        ...biData,
        host_app_id: appDefinitionId,
      };

      bi.addonInstallTry(biPayload);

      await handleInstall(appDefinitionId);

      bi.addonInstallSuccess(biPayload);

      await openAddonPanel(appDefinitionId);
    },
    [bi, openAddonPanel, handleInstall],
  );

  const uninstallAddon = useCallback(
    async (
      appDefinitionId: string,
      biData: { origin: BaseAddonEventPayload['origin'] },
    ) => {
      const biPayload: UninstallAddonEventPayload = {
        ...biData,
        host_app_id: appDefinitionId,
      };

      bi.addonUninstallTry(biPayload);

      await handleUninstall(appDefinitionId);

      bi.addonUninstallSuccess(biPayload);

      await closeAddonPanel(appDefinitionId);
    },
    [bi, closeAddonPanel, handleUninstall],
  );

  const getInstalledAddons = useCallback(() => {
    return state.addons.filter(({ appDefinitionId }) =>
      isInstalled(appDefinitionId),
    );
  }, [state.addons, isInstalled]);

  return {
    ...state,
    isInstallationInProgress,
    openAddonPanel,
    getInstalledAddons,
    fetchAddons,
    installAddon,
    uninstallAddon,
  };
};
